import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useSnackbar } from "notistack";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { WorkspaceContext } from "../../../contexts/WorkspaceContext";
import { WorkspacesContext } from '../../../contexts/WorkspacesContext';
import { updateWorkspaceFunc } from "../../../lib/helper";
import { defaultStages } from "../../../shared/dashboard";
import { Workspace } from "../../../types/files";
import MultiSelector from "../../atoms/MultiSelector";

const useStyles = makeStyles((theme) => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: 550,
            height: 'fit-content',
            padding: '16px 24px',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.3rem',
        gap: '0.5rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 0',
        height: '100%',
        overflowY: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: 0,
    },
    label: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['800'],
    },
    caption: {
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        color: theme.colors.neutral['600'],
    },
    sublabel: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: '600',
        color: theme.palette.primary.main,
    },
    inputField: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: 8,
            padding: '0 8px !important',
            background: '#fff',
            "& > input, > textarea": {
                fontFamily: 'Lato',
                fontSize: '1.2rem',
                color: '#048290',
                padding: 'unset !important',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
    existingIcon: {
        width: 20,
        height: 20,
        color: theme.colors.error['700'],
    },
    existingCaption: {
        fontFamily: 'Inter',
        fontWeight: 500,
        fontSize: '0.9rem',
        color: theme.colors.error['700'],
    },
    commonButton: {
        minWidth: 80,
        borderRadius: 32,
        textTransform: 'none',
        fontWeight: 'bold',
    },
    cancelButton: {
        width: 100,
        borderRadius: 32,
        borderColor: theme.colors.neutral['100'],
        backgroundColor: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
}));

const SharedWorkspaceSettings: React.FC<{
    isOpen: boolean,
    sharedWorkspace: Workspace,
    onClose: () => void,
}> = ({ isOpen, sharedWorkspace, onClose }) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { setWorkspace } = useContext(WorkspaceContext);
    const { tags } = useContext(GroupSettingsContext);
    const { workspaces, setWorkspaces } = useContext(WorkspacesContext);

    const [saving, setSaving] = useState<boolean>(false);
    const [saveReady, setSaveReady] = useState<boolean>(false);
    const [existing, setExisting] = useState<boolean>(false);
    const [sector, setSector] = useState<string[]>([]);
    const [stage, setStage] = useState<string[]>([]);
    const [loaded, setLoaded] = useState<boolean>(false);

    const nameRef = useRef<TextFieldProps>(null);

    const workspaceNames = useMemo(() => workspaces.map(ws => ws.name.trim().toLowerCase()), [workspaces]);

    const handleSave = useCallback(() => {
        const name = (nameRef.current?.value as string || sharedWorkspace?.name || '').trim();

        setSaving(true);

        const filteredWorkspaceNames = workspaceNames.filter(wsName => wsName !== sharedWorkspace?.name?.trim().toLowerCase());

        if (filteredWorkspaceNames.includes(name.trim().toLowerCase())) {
            setExisting(true);
            setSaving(false);
        } else {
            updateWorkspaceFunc({...sharedWorkspace, name, tags: sector, investmentStages: stage})
                .then((updatedWorkspace?: Workspace) => {
                    setSaving(false);
                    if (!updatedWorkspace?.id) {
                        setExisting(true);
                    } else {
                        setWorkspace(updatedWorkspace);
                        setWorkspaces(prev => prev.map(ws =>
                            ws.id === sharedWorkspace.id! ? {...updatedWorkspace} : ws
                        ));
                        enqueueSnackbar('Shared workspace updated successfully!', {
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'right',
                            },
                            autoHideDuration: 4000,
                        });
                        onClose();
                    }
                });
        }
    // eslint-disable-next-line
    }, [sector, stage, sharedWorkspace, workspaceNames]);

    const handleClear = useCallback(() => {
        if (nameRef.current)
            nameRef.current.value = '';
        setExisting(false);
    }, []);

    useEffect(() => {
        if (!!sharedWorkspace && !loaded) {
            setSector(sharedWorkspace.tags ?? []);
            setStage(sharedWorkspace.investmentStages ?? []);
            setLoaded(true);
        }
    // eslint-disable-next-line
    }, [sharedWorkspace]);

    return (<>
        <Dialog className={classes.dialog} open={isOpen} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                <SettingsOutlinedIcon sx={{ width: 28, height: 28, fill: "#7bd4d4" }} />
                <span>{'Shared workspace settings'}</span>
            </DialogTitle>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogContent className={classes.dialogContent}>
                <Stack direction="column" alignItems="stretch" justifyContent="space-between" width="100%">
                    <Stack direction="row" spacing={1} alignItems="center" width="100%">
                        <Typography className={classes.label} width="85px">{'Name'}</Typography>
                        <TextField variant="outlined"
                            className={classes.inputField}
                            placeholder={'(required)'}
                            defaultValue={sharedWorkspace?.name || ''}
                            onChange={() => {setExisting(false); setSaveReady(!!nameRef.current?.value);}}
                            inputRef={nameRef}
                            disabled={saving}
                            rows={1}
                            autoFocus />
                    </Stack>
                    <Box mt={2} />
                    <Typography className={classes.label}>
                        {'Associated tags: '}
                    </Typography>
                    <Typography className={classes.caption}>
                        {'Select 2 sector tags minimum.'}
                    </Typography>
                     <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%" overflow="hidden" mt={1}>
                        <Typography className={classes.sublabel}>
                            {'sector:'}
                        </Typography>
                        <MultiSelector
                            mainOptions={tags}
                            selections={sector}
                            onSelection={(selections) => {
                                setSector(selections);
                                setSaveReady(!!nameRef.current?.value);
                            }} withSearch />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%" overflow="hidden" mt={2}>
                        <Typography className={classes.sublabel}>
                            {'stage:'}
                        </Typography>
                        <MultiSelector
                            mainOptions={defaultStages}
                            selections={stage}
                            onSelection={(selections) => {
                                setStage(selections);
                                setSaveReady(!!nameRef.current?.value);
                            }} />
                    </Stack>
                </Stack>
                {existing && (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" mt={4} mb={2}>
                        <ErrorOutlineIcon className={classes.existingIcon} />
                        <Typography className={classes.existingCaption}>
                            {'Shared workspace already exists!'}
                        </Typography>
                    </Stack>
                )}
            </DialogContent>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogActions className={classes.dialogActions}>
                <Button
                    variant="outlined"
                    className={classes.cancelButton}
                    onClick={() => onClose()}
                > {'Cancel'} </Button>
                {existing ? (
                    <Button
                        variant="outlined"
                        className={classes.commonButton}
                        onClick={handleClear}
                    > {'Clear'} </Button>
                ) : (
                    <LoadingButton
                        variant="contained"
                        className={classes.commonButton}
                        loading={saving}
                        disabled={!saveReady}
                        onClick={handleSave}
                    > {'Save'} </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    </>);
}

export default SharedWorkspaceSettings;